import type { TranslationMessages } from '../types';

export const products: TranslationMessages = {
  edit: 'Edit Product',
  create: 'Create Product',
  editDescription: 'Update product details',
  createDescription: 'Add a new product to the system',
  basicInfo: 'Basic Information',
  fields: {
    name: 'Name',
    namePlaceholder: 'Enter product name',
    description: 'Description',
    descriptionPlaceholder: 'Enter product description',
    categoryId: 'Category',
    categoryIdPlaceholder: 'Select category (optional)',
    price: 'Price',
    pricePlaceholder: 'Enter price',
    currency: 'Currency',
    currencyPlaceholder: 'Select currency',
    recipeId: 'Recipe',
    recipeIdPlaceholder: 'Select recipe (optional)',
    markupPercentage: 'Markup Percentage (%)',
    imageId: 'Image',
  },
  recipe: {
    noneStockBased: 'None (Stock-based)',

    selected: 'Recipe Selected',
    notSelected: 'No Recipe - Direct Inventory',
    validation: {
      valid: 'Recipe is valid',
      invalid: 'Recipe validation failed',
    },
    cost: 'Estimated cost',
    ingredients: 'ingredients',
    info: 'When this product is sold, inventory will be automatically deducted based on the recipe ingredients.',
    noRecipeInfo:
      'No recipe selected. This product will use direct inventory mapping instead of recipe-based deduction.',
  },
  cost: {
    title: 'Cost Calculation',
    calculating: 'Calculating...',
    calculate: 'Calculate Cost',
    baseCost: 'Base Cost',
    suggestedPrice: 'Suggested Price',
    suggestedFormula:
      'Suggested price = Base cost × (1 + %{markupPercentage}%) = %{suggestedPrice} %{currency}',
    breakdown: 'Cost Breakdown',
    useSuggestedPrice: 'Use Suggested Price',
  },
  errors: {
    selectRecipeFirst: 'Please select a recipe first',
    saveProductFirst: 'Please save the product first to calculate cost',
  },
  delete_title: 'Delete Product',
  delete_confirm:
    'Are you sure you want to delete this product? This action cannot be undone.',
  delete_error: 'Failed to delete product. Please try again.',
  delete_error_permission:
    'You do not have permission to delete products. Please contact your administrator.',
  delete_success: 'Product deleted successfully',

  name: 'Product |||| Products',
  notifications: {
    title: 'Notifications',
    empty: 'No notifications',
    markAsRead: 'Mark as read',
    markAllAsRead: 'Mark all as read',
  },
  configuration: {
    name: 'Configuration',
    receipt: 'Receipt Settings',
    thankYouMessage: 'Thank You Message',
    language: 'Language',
    theme: {
      name: 'Theme',
      light: 'Light',
      dark: 'Dark',
    },
    qrCode: {
      label: 'QR Code',
      description:
        'Upload a QR code image that will be displayed on the receipt',
      thankYouMessage:
        'Thank you for being with us. For any feedback or suggestions, please scan the QR code below. Have a wonderful day!',
    },
  },
  costs: {
    title: 'Costs',
    name: 'Cost Management',
    create_cost: 'Add Cost',
    subtitle: 'Track and manage business expenses',
    versions: {
      modern: 'Modern Cost Management',
    },
    stats: {
      total_costs: 'Total Costs',
      entries: 'entries',
      this_month: 'This Month',
      current_month_expenses: 'Current month expenses',
      categories: 'Categories',
      active_categories: 'Active categories',
      average_cost: 'Average Cost',
      per_entry: 'Per entry',
    },
    search_placeholder: 'Search costs...',
    all_categories: 'All Categories',
    no_costs_found: 'No costs found',
    try_adjusting_filters: 'Try adjusting your search or filter criteria',
    add_first_cost: 'Add First Cost',
    no_permission_edit: 'You do not have permission to edit cost entries.',
    fields: {
      description: 'Description',
      amount: 'Amount',
      category: 'Category',
      method: 'Payment Method',
      date: 'Date',
      notes: 'Notes',
      payment_method: 'Payment Method',
    },
    unknown: 'Unknown',
    messages: {
      costCreated: 'Cost entry created successfully',
      costUpdated: 'Cost entry updated successfully',
      costDeleted: 'Cost entry deleted successfully',
    },
    create: {
      title: 'Create Cost Entry',
      subtitle: 'Add a new expense to track',
      no_permission: 'You do not have permission to create cost entries.',
      basic_information: 'Basic Information',
      description_placeholder: 'Enter cost description...',
      description_required: 'Description is required',
      amount_placeholder: '0.00',
      amount_required: 'Amount is required',
      amount_positive: 'Amount must be positive',
      date_required: 'Date is required',
      category_payment: 'Category & Payment',
      select_category: 'Select a category',
      search_categories: 'Search categories...',
      no_category_found: 'No category found.',
      category_required: 'Category is required',
      payment_method_placeholder: 'Select payment method',
      payment_method_none: 'None',
      payment_method_cash: 'Cash',
      payment_method_bank: 'Bank Transfer',
      payment_method_credit_card: 'Credit Card',
      payment_method_vnpay: 'VnPay',
      payment_method_momo: 'MoMo',
      payment_method_zalopay: 'ZaloPay',
      payment_method_other: 'Other',
      receipt: 'Receipt',
      receipt_preview: 'Receipt preview',
      upload_receipt: 'Upload Receipt',
      capture_receipt: 'Capture Receipt',
      capture_button: 'Capture',
      camera_instructions:
        'Position the receipt in front of the camera and click Capture when ready',
      loading_camera: 'Loading camera...',
      uploading_receipt: 'Uploading...',
      receipt_description:
        'Upload or capture a photo of the receipt (optional)',
      additional_notes: 'Additional Notes',
      notes_placeholder: 'Add any additional notes or details...',
      summary: 'Summary',
      not_selected: 'Not selected',
      save_button: 'Save Cost Entry',
    },
  },
  cost_categories: {
    title: 'Cost Categories',
    subtitle: 'Manage cost categories for better organization',
    new_category: 'Add Category',
    search_placeholder: 'Search categories...',
    no_results: 'No categories found',
    try_adjusting_search: 'Try adjusting your search criteria',
    add_first_category: 'Add First Category',
    created: 'Created',
    default: 'Default',
    custom: 'Custom',
    filter_categories: 'Filter Categories',
    default_status: 'Default Status',
    select_default_status: 'Select default status',
    all_categories: 'All Categories',
    default_categories: 'Default Categories',
    custom_categories: 'Custom Categories',
    clear_all: 'Clear All',
    apply_filters: 'Apply Filters',
    no_permission_delete:
      'You do not have permission to delete cost categories.',
    delete_confirm: 'Are you sure you want to delete this category?',
    delete_confirm_irreversible:
      'Are you sure you want to delete this category? This action cannot be undone.',
    delete_error: 'Failed to delete category. Please try again.',
    create_title: 'Create Cost Category',
    create_subtitle: 'Add a new cost category for better organization',
    edit_title: 'Edit Cost Category',
    edit_subtitle: 'Update category information',
    basic_information: 'Basic Information',
    name_placeholder: 'Enter category name...',
    name_required: 'Name is required',
    description_placeholder: 'Enter category description...',
    description_required: 'Description is required',
    parent_placeholder: 'Enter parent category (optional)...',
    set_as_default: 'Set as default category',
    summary: 'Summary',
    not_entered: 'Not entered',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    save_category: 'Save Category',
    update_category: 'Update Category',
    delete_category: 'Delete Category',
    no_permission_create:
      'You do not have permission to create cost categories.',
    no_permission_edit:
      'You do not have permission to edit cost categories.',
    create_error: 'Failed to create cost category. Please try again.',
    update_error: 'Failed to update cost category. Please try again.',
    load_error: 'Failed to load category data. Please try again.',
    categoryCreated: 'Cost category created successfully',
  },

  costCategories: {
    name: 'Cost Categories',
    versions: {
      modern: 'Modern Cost Categories',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      parent: 'Parent Category',
      isDefault: 'Default Category',
    },
    messages: {
      categoryCreated: 'Cost category created successfully',
      categoryUpdated: 'Cost category updated successfully',
      categoryDeleted: 'Cost category deleted successfully',
    },
  },
  inventory: {
    name: 'Inventory',
    add: 'Add Inventory',
    scanQR: 'Update Stock',
    edit: 'Edit Inventory',
    deleteAction: 'Delete Inventory',
    create: 'Create Inventory',
    editDescription: 'Update inventory item details',
    createDescription: 'Add a new inventory item to the system',
    list: 'Inventory Items',
    search: 'Search inventories...',
    warehouse: {
      name: 'Warehouse',
      placeholder: 'Select warehouse',
    },
    category: {
      name: 'Category',
      placeholder: 'Select category',
    },
    ingredient: {
      name: 'Ingredient',
      placeholder: 'Select ingredient (optional)',
      none: 'No ingredient',
      quickCreate: 'Quick create ingredient',
    },
    product: {
      name: 'Product',
      placeholder: 'Select product (optional)',
      none: 'No product',
      help: 'Select a product for basic items (e.g., Fanta, Surprise, Pepsi). Leave empty if linking to an ingredient.',
      searchPlaceholder: 'Search products...',
    },
    filter: 'Filter',
    clear: 'Clear',
    all: 'All',
    status: 'Status',
    price: 'Price',
    basicInfo: 'Basic Information',
    quantityPricing: 'Quantity & Pricing',
    dimensions: 'Package Dimensions',
    fields: {
      code: 'Code',
      codePlaceholder: 'Enter inventory code',
      name: 'Name',
      description: 'Description',
      quantity: 'Quantity',
      totalPrice: 'Total Price',
      currency: 'Currency',
      width: 'Width',
      height: 'Height',
      weight: 'Weight',
      volume: 'Volume',
      isPackage: 'Is Package',
      namePlaceholder: 'Enter inventory name',
      descriptionPlaceholder: 'Enter inventory description',
      itemCount: 'Items per Package',
      itemCountPlaceholder: 'e.g., 12',
      itemCountHelp:
        'Optional: Number of individual items contained in each package',
      quantityAnchor: 'Quantity Calculation Anchor',
      quantityAnchorPlaceholder: 'Select anchor (optional)',
      quantityAnchorHelp:
        'Select which property to use for automatic quantity calculation. When set, quantity will be calculated from remaining values and cannot be edited manually.',
      noAnchor: 'No anchor (manual quantity)',
      anchorItemCount: 'Item Count',
      anchorWeight: 'Weight',
      anchorVolume: 'Volume',
      calculated: 'Calculated',
      quantityHelp:
        'Total items: {total} ({quantity} packages × {itemCount} items)',
      quantityCalculatedHelp:
        'Quantity is automatically calculated from the anchor property',
      currentStock:
        'Current stock: {total} items ({quantity} packages × {itemCount} items per package)',
      currentQuantity: 'Current quantity: {quantity}',
      updateStock: 'Update Stock',
      remainingItems: 'Remaining Items',
      remainingItemsHelp:
        'Total remaining items. Quantity will be calculated as: remainingItems / itemCount',
      remainingWeight: 'Remaining Weight',
      remainingWeightHelp:
        'Total remaining weight. Quantity will be calculated as: remainingWeight / weight',
      remainingVolume: 'Remaining Volume',
      remainingVolumeHelp:
        'Total remaining volume. Quantity will be calculated as: remainingVolume / volume',
    },
    code: 'Code',
    quantity: 'Quantity',
    totalPrice: 'Total Price',
    currency: 'Currency',
    scanQrCode: 'Scan Inventory QR Code',
    scannerReady: 'Scanner Ready',
    scanning: 'Scanning...',
    processing: 'Processing...',
    scannerInstructions: 'Scanner Instructions',
    pointCamera: 'Point camera at inventory QR code',
    ensureGoodLighting: 'Ensure good lighting',
    holdSteady: 'Hold steady until scan completes',
    invalidQRFormat:
      'Invalid QR code format. Please scan an inventory QR code.',
    customerQRDetected:
      'This QR code is for customer check-in, not inventory management',
    qrProcessingFailed: 'Failed to process QR code',
    scannerError: 'Scanner error',
    messages: {
      created: 'Inventory item created successfully',
      updated: 'Inventory item updated successfully',
      deleted: 'Inventory item deleted successfully',
      error_creating: 'Error creating inventory item',
      error_updating: 'Error updating inventory item',
      error_deleting: 'Error deleting inventory item',
      error_loading: 'Error loading inventory items',
      confirm_delete:
        'Are you sure you want to delete this inventory item?',
    },
    qrCode: 'QR Code',
    printQR: 'Print QR Code',
    provider: {
      name: 'Provider',
      placeholder: 'Select provider (optional)',
      none: 'No provider',
    },
    deleteTitle: 'Delete Inventory Item',
    delete: {
      message:
        'Are you sure you want to delete this inventory item? This action cannot be undone.',
    },
    qrCodeManager: 'QR Code Manager',
    currentQuantity: 'Current Quantity',
    generating: 'Generating...',
    generate: 'Generate',
    downloadQR: 'Download QR Code',
    scanQRCode: 'Scan QR Code',
    scan: 'Scan',
    generateQRCode: 'Generate QR Code',
    scanInstructions: 'Scan Instructions',
    scanForCounting: 'Scan for counting inventory',
    scanForDeduction: 'Scan for stock deduction',
    scanForAdjustment: 'Scan for stock adjustment',
  },
  menu: {
    sales: 'Sales',
    catalog: 'Catalog',
    customers: 'Customers',
    library: 'Library',
    memberships: 'Memberships',
    staff: 'Staff Management',
    staff_profiles: 'Staff Profiles',
    organization: 'Organization',
    financial: 'Financial Management',
    system: 'System',
    zalo: 'Zalo Integration',
  },
  events: {
    review: {
      title: 'Posted review on "%{product}"',
    },
    order: {
      title: 'Ordered 1 poster |||| Ordered %{smart_count} posters',
    },
  },
  receipt: {
    thankYou: 'Thank you for your support!',
  },
  recipes: {
    name: 'Recipe Management',
    add: 'Add Recipe',
    edit: 'Edit Recipe',
    create: 'Create Recipe',
    list: 'Recipes',
    search: 'Search recipes...',
    status: 'Status',
    cost: 'Cost',
    valid: 'Valid',
    invalid: 'Invalid',
    empty: 'No recipes found',
    basicInformation: 'Basic Information',
    placeholders: {
      enterName: 'Enter recipe name',
      selectCategory: 'Select category',
      enterDescription: 'Enter recipe description',
      enterImageId: 'Enter image ID (optional)',
      selectIngredient: 'Select ingredient',
      enterAmount: '0',
    },
    delete: {
      title: 'Delete Recipe',
      confirm:
        'Are you sure you want to delete "%{name}"? This action cannot be undone.',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      imageId: 'Image',
      productCategoryId: 'Product Category',
      ingredients: 'Ingredients',
      amount: 'Amount',
    },
    ingredient: {
      quickCreate: 'Quick create ingredient',
    },
    ingredients: 'Ingredients',
    validation: {
      title: 'Recipe Validation Details',
      description: 'Why "%{name}" is invalid:',
      details: 'View validation details',
      noErrors: 'No validation errors found.',
      errors: 'Validation Errors:',
      missingMappings: 'Missing Inventory Mappings:',
      missingMappingItem:
        'Ingredient "%{ingredientName}" has no inventory mapping. Please map this ingredient to an inventory item.',
      errorNoMapping:
        'Ingredient "%{ingredientName}" has no inventory mapping',
      fixInstructions:
        'To fix these issues, you can automatically create inventory items for missing ingredients, or manually create them in the inventory management page.',
      goToMappings: 'Go to Inventory Management',
      autoFix: {
        button: 'Auto-fix Missing Mappings',
        processing: 'Processing...',
        warning:
          'Note: Auto-fix creates basic inventory items with default settings. Package information (item count, dimensions, weight, volume) must be reviewed and configured manually by the store manager.',
        success:
          'Successfully created %{count} inventory item(s). Please review and configure package details manually.',
        error: 'Failed to auto-fix mappings: %{message}',
        partialError:
          'Created %{success} item(s), but %{error} failed: %{details}',
        noWarehouse:
          'Cannot auto-fix: No warehouses found. Please create a warehouse first.',
        noCategory:
          'Cannot auto-fix: No inventory categories found. Please create an inventory category first.',
      },
    },
  },
  ingredients: {
    name: 'Ingredient Management',
    add: 'Add Ingredient',
    edit: 'Edit Ingredient',
    create: 'Create Ingredient',
    list: 'Ingredients',
    search: 'Search ingredients...',
    empty: 'No ingredients found',
    delete: {
      title: 'Delete Ingredient',
      confirm:
        'Are you sure you want to delete this ingredient? This action cannot be undone.',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      imageId: 'Image',
      recipeId: 'Recipe (Optional)',
      recipe: 'Recipe',
    },
    recipe: {
      none: 'No recipe',
      hasRecipe: 'Has recipe',
      help: 'If this ingredient is composed from other ingredients, select a recipe. This allows nested recipes up to 2 levels.',
    },
    placeholders: {
      selectRecipe: 'Select recipe',
      searchRecipe: 'Search recipes...',
    },
  },
  ingredientInventoryMappings: {
    name: 'Ingredient-Inventory Mapping',
    add: 'Add Mapping',
    edit: 'Edit Mapping',
    delete: 'Delete Mapping',
    delete_confirm: 'Are you sure you want to delete this mapping?',
    create: 'Create Mapping',
    list: 'Mappings',
    description: {
      update: 'Update ingredient-inventory mapping',
      create:
        'Create a new mapping between an ingredient and inventory item',
    },
    search: {
      label: 'Search',
      placeholder: 'Search by ingredient or inventory...',
      filterByIngredient: 'Filter by Ingredient',
      allIngredients: 'All ingredients',
    },
    placeholders: {
      selectIngredient: 'Select ingredient',
      selectInventory: 'Select inventory item',
      selectWarehouse: 'Select warehouse (optional)',
    },
    fields: {
      ingredientId: 'Ingredient',
      inventoryId: 'Inventory Item',
      warehouseId: 'Warehouse',
      priority: 'Priority',
      conversionFactor: 'Conversion Factor',
      isActive: 'Active',
    },
  },
  customers: {
    name: 'Customer |||| Customers',
    title: 'Customers',
    create: 'Create Customer',
    edit: 'Edit Customer',
    create_description: 'Create a new customer account',
    edit_description: 'Update customer information',
    fields: {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      city: 'City',
      stateAbbr: 'State/Province',
      zipcode: 'Zip Code',
      birthday: 'Birthday',
      totalSpent: 'Total Spent',
      nbCommands: 'Orders',
      lastSeen: 'Last Seen',
      orders: 'Orders',
      first_seen: 'First Seen',
      groups: 'Groups',
      last_seen: 'Last Seen',
      last_seen_gte: 'Visited Since',
      name: 'Name',
      total_spent: 'Total Spent',
      password: 'Password',
      confirm_password: 'Confirm Password',
      customerPoints: 'Points: %{points}',
      otp: 'OTP Code',
    },
    filters: {
      last_visited: 'Last Visit',
      today: 'Today',
      this_week: 'This Week',
      last_week: 'Last Week',
      this_month: 'This Month',
      last_month: 'Last Month',
      earlier: 'Earlier',
      has_ordered: 'Has Ordered',
      has_newsletter: 'Subscribed to Newsletter',
      group: 'Group',
    },
    fieldGroups: {
      identity: 'Personal Information',
      address: 'Address',
      stats: 'Statistics',
      history: 'History',
      password: 'Password',
      change_password: 'Change Password',
    },
    page: {
      delete: 'Delete Customer',
      showing: 'Showing',
      to: 'to',
      of: 'of',
      page: 'Page',
      customers: 'customers',
    },
    errors: {
      password_mismatch: 'Password confirmation does not match password.',
    },
    actions: {
      verifyOtp: 'Verify OTP',
    },
  },
  recipe_based: 'Recipe-based',
  stock_based: 'Stock-based',
  };
